import { useEffect, useState } from "react";
import { api } from "../lib/api";

export default function RecidivesView() {
  const [stats, setStats] = useState(null);
  const [periodKey, setPeriodKey] = useState("all");

  useEffect(() => {
    api.get("/admin/incidents/stats").then(({ data }) => setStats(data));
  }, []);

  if (!stats) {
    return <div className="mt-8 text-[var(--ink-soft)]">Chargement...</div>;
  }

  const countsFor = (r) => {
    if (periodKey === "all") return { comportement: 0, ...r.total };
    const p = r.by_period?.[periodKey] || { travail: 0, affaires: 0, comportement: 0 };
    return { comportement: 0, ...p, all: p.travail + p.affaires + (p.comportement || 0) };
  };
  const rows = stats.rows
    .map((r) => ({ ...r, counts: countsFor(r) }))
    .filter((r) => r.counts.all > 0)
    .sort((a, b) => b.counts.all - a.counts.all);
  const periodLabel = periodKey === "all" ? "année scolaire" : stats.periods.find((p) => p.key === periodKey)?.name;

  return (
    <div data-testid="recidives-view">
      <div className="mt-6 flex flex-wrap gap-2" data-testid="recidives-period-filter">
        <Chip active={periodKey === "all"} label="Année complète" onClick={() => setPeriodKey("all")} testId="recidive-chip-all" />
        {stats.periods.map((p) => (
          <Chip key={p.key} active={periodKey === p.key} label={p.name} sub={`${p.start} → ${p.end}`} onClick={() => setPeriodKey(p.key)} testId={`recidive-chip-${p.key}`} />
        ))}
      </div>

      <RecidivesTable rows={rows} periodLabel={periodLabel} />
    </div>
  );
}

function RecidiveRow({ r, index }) {
  return (
    <tr className="border-b border-[var(--parchment-2)]/60" data-testid={`recidive-row-${r.student.id}`}>
      <td className="py-3 font-mono-spec text-xs text-[var(--muted)]">{String(index + 1).padStart(2, "0")}</td>
      <td className="py-3 font-medium">{r.student.last_name.toUpperCase()} {r.student.first_name}</td>
      <td className="py-3 text-[var(--ink-soft)]">{r.class?.name || "—"}</td>
      <td className="py-3 text-center"><Count value={r.counts.travail} color="var(--late)" /></td>
      <td className="py-3 text-center"><Count value={r.counts.affaires} color="var(--just)" /></td>
      <td className="py-3 text-center"><Count value={r.counts.comportement} color="var(--maroon)" /></td>
      <td className="py-3 text-center">
        <span
          className={`inline-flex items-center justify-center min-w-[2.2rem] px-2 py-1 font-mono-spec font-bold text-sm ${
            r.counts.all >= 3 ? "text-white" : "text-[var(--ink)]"
          }`}
          style={r.counts.all >= 3 ? { background: "var(--late)" } : { background: "var(--parchment)" }}
          data-testid={`recidive-total-${r.student.id}`}
        >
          {r.counts.all}
        </span>
      </td>
    </tr>
  );
}

function RecidivesTable({ rows, periodLabel }) {
  return (
    <div className="mt-6 bg-white border border-[var(--parchment-2)] p-6 overflow-x-auto">
      <div className="eyebrow mb-4">Récidives · {periodLabel} — les élèves les plus signalés en premier</div>
      <table className="w-full text-sm" data-testid="recidives-table">
        <thead>
          <tr className="border-b border-[var(--parchment-2)] text-left">
            <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">#</th>
            <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Élève</th>
            <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Classe</th>
            <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium text-center">Travail non fait</th>
            <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium text-center">Affaires oubliées</th>
            <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium text-center">Comportement</th>
            <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium text-center">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => <RecidiveRow key={r.student.id} r={r} index={i} />)}
          {rows.length === 0 && (
            <tr><td colSpan={7} className="py-6 text-center text-[var(--ink-soft)] italic">Aucun signalement sur cette période.</td></tr>
          )}
        </tbody>
      </table>
      <div className="mt-4 text-xs text-[var(--ink-soft)] italic">
        Un total en rouge signale 3 signalements ou plus sur la période — élève à convoquer.
      </div>
    </div>
  );
}

function Count({ value, color }) {
  if (!value) return <span className="text-[var(--muted)] font-mono-spec">0</span>;
  return (
    <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 text-white font-mono-spec font-semibold text-xs" style={{ background: color }}>
      {value}
    </span>
  );
}

function Chip({ active, label, sub, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      title={sub}
      className={`px-3 py-1.5 text-xs font-medium border transition ${
        active ? "bg-[var(--maroon)] text-white border-[var(--maroon)]" : "bg-white text-[var(--ink-soft)] border-[var(--parchment-2)] hover:border-[var(--ink)]"
      }`}
    >
      {label}
    </button>
  );
}
